import { defineStore } from "pinia";
import { login, verify, wecomLogin } from "@/api/login.js";
import { getUserInfo } from "@/api/accounts.js";
import { ROLE_GROUPS, ROLE_KEYS } from "@/config/rbac.js";
import { ACCESS_TOKEN } from "@/store/mutation-types.js";
import router from "@/router/index.js";
import { clearCookie, setCookie } from "@/utils/storage.js";

export const useUserStore = defineStore("user", {
  state: () => ({
    token: "",
    name: "",
    welcome: "",
    avatar: "",
    roles: [],
    userInfo: {},
    /** 是否已拉取过 user-profile（避免无 page_permissions 时路由守卫死循环请求） */
    profileLoaded: false
  }),
  getters: {
    pagePermissions: (state) => state.userInfo.page_permissions || [],
    actionPermissions: (state) => state.userInfo.action_permissions || [],
    roleNames: (state) => (state.userInfo.roles || []).map((item) => item.name).filter(Boolean),
    hasRole() {
      return (role) => {
        if (Array.isArray(role)) {
          return role.some((item) => this.hasRole(item));
        }
        return this.roleNames.includes(role);
      };
    },
    hasAnyRole() {
      return (roles = []) => roles.some((role) => this.hasRole(role));
    },
    hasPermission() {
      return (permission) =>
        this.pagePermissions.includes(permission) || this.actionPermissions.includes(permission);
    },
    hasAnyPermission() {
      return (permissions = []) => permissions.some((permission) => this.hasPermission(permission));
    },
    isAdmin() {
      return this.hasRole(ROLE_GROUPS.ADMIN);
    },
    isPM() {
      return this.hasRole(ROLE_KEYS.PM);
    },
    isPA() {
      return this.hasRole(ROLE_KEYS.PA);
    },
    PA() {
      return this.isPA;
    },
    isFinance() {
      return this.hasRole(ROLE_KEYS.FINANCE);
    },
    isSales() {
      return this.hasRole(ROLE_KEYS.SALES);
    },
    isGuest() {
      return this.hasRole(ROLE_KEYS.GUEST);
    },
    roleFilter() {
      return undefined;
    }
  },
  actions: {
    setToken(token) {
      this.token = token;
    },
    setName({ name, welcome }) {
      this.name = name;
      this.welcome = welcome;
    },
    setAvatar(avatar) {
      this.avatar = avatar;
    },
    setRoles(roles) {
      this.roles = roles;
    },
    setInfo(info) {
      this.userInfo = info;
    },
    setProfileLoaded(loaded) {
      this.profileLoaded = !!loaded;
    },
    async login(params) {
      const loginParams = { ...params };
      const loginFn = loginParams.type === "wecom" ? wecomLogin : login;
      delete loginParams.type;
      const res = await loginFn(loginParams);
      if (res.code !== 200) {
        return res;
      }
      this.setToken(res.data.token);
      this.setRoles(res.data.roles || []);
      this.setProfileLoaded(false);
      clearCookie(ACCESS_TOKEN);
      setCookie(ACCESS_TOKEN, res.data.token, 24 * 30);
      return res;
    },
    verify(data) {
      return verify(data);
    },
    async getInfo() {
      const res = await getUserInfo();
      if (res?.data) {
        this.setRoles(res.data.roles || []);
        this.setInfo(res.data);
      }
      this.setProfileLoaded(true);
      return res;
    },
    logout() {
      this.setToken("");
      this.setRoles([]);
      this.setInfo({});
      this.setProfileLoaded(false);
      clearCookie(ACCESS_TOKEN);
      return router.push("/users/sign_in");
    }
  },
  persist: {
    key: "user",
    storage: sessionStorage,
    pick: ["userInfo"]
  }
});

export default useUserStore;
